<template>
  <div class="maintenance-container vh-100">
    <div class="maintenance-card">
      <img src="@/assets/images/logo.png" alt="Logo" class="maintenance-logo" />
      <h1>Sitio en Mantenimiento</h1>
      <p class="maintenance-message">{{ message || defaultMessage }}</p>
      
      <div class="maintenance-action">
        <button @click="goToAdminLogin" class="btn btn-primary">
          Ingresar (Administradores)
        </button>
        <button v-if="hasBypassPassword" @click="showPasswordModal = true" class="btn btn-outline-primary">
          Acceder con contraseña
        </button>
      </div>
    </div>
  </div>
  
  <!-- Modal para ingresar contraseña de bypass -->
  <div v-if="showPasswordModal" class="modal-overlay" @click.self="showPasswordModal = false">
    <div class="modal-content">
      <h3>Acceso con contraseña</h3>
      <p>Ingrese la contraseña para acceder al sitio durante el mantenimiento</p>
      
      <div class="form-group">
        <input 
          type="password" 
          v-model="password" 
          class="form-control" 
          placeholder="Contraseña de acceso"
          @keyup.enter="validatePassword"
        />
      </div>
      
      <div v-if="error" class="error-message">
        {{ error }}
      </div>
      
      <div class="modal-actions">
        <button @click="showPasswordModal = false" class="btn btn-secondary">
          Cancelar
        </button>
        <button @click="validatePassword" class="btn btn-primary" :disabled="validating">
          <span v-if="validating" class="spinner"></span>
          <span v-else>Verificar</span>
        </button>
      </div>
    </div>
  </div>

  <div class="loader-container" v-if="loadingPage">
    <div class="loader"></div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import maintenanceService from '@/services/maintenanceService';

export default {
  name: 'MaintenancePage',
  setup() {
    const router = useRouter();
    const message = ref('');
    const defaultMessage = 'Estamos realizando mejoras en nuestro sistema. Disculpe las molestias.';
    const loadingPage = ref(false);
    const showPasswordModal = ref(false);
    const hasBypassPassword = ref(false);
    const password = ref('');
    const error = ref('');
    const validating = ref(false);
    
    // Navegar a la página de login de administrador
    const goToAdminLogin = () => {
      // Añadir un parámetro de ruta para indicar que viene de la página de mantenimiento
      router.push({ 
        name: 'LoginForm', 
        query: { 
          redirect: '/', // Redirección después de login exitoso
          from: 'maintenance', // Indicar que viene desde mantenimiento
          t: Date.now() // Evitar caché
        } 
      });
    };
    
    // Verificar el estado de mantenimiento al montar el componente
    const checkMaintenanceStatus = async () => {
      loadingPage.value = true;
      try {
        const status = await maintenanceService.getMaintenanceStatus();
        
        if (!status.maintenance_mode) {
          // Si el sitio ya no está en mantenimiento, redirigir a la página principal
          router.push({ name: 'Home' });
          return;
        }
        
        // Actualizar datos del modo mantenimiento
        message.value = status.message || defaultMessage;
        hasBypassPassword.value = status.has_bypass_password || false;
        
        // Verificar si el usuario ya validó su acceso
        const hasValidBypass = maintenanceService.hasValidatedBypass();
        if (hasValidBypass) {
          router.push({ name: 'Home' });
        }
      } catch (error) {
        console.error('Error al verificar el estado de mantenimiento:', error);
      } finally {
        loadingPage.value = false;
      }
    };
    
    // Validar la contraseña de bypass
    const validatePassword = async () => {
      if (!password.value.trim()) {
        error.value = 'Por favor ingrese una contraseña';
        return;
      }
      
      validating.value = true;
      error.value = '';
      
      try {
        const result = await maintenanceService.validateBypassPassword(password.value);
        
        if (result.valid) {
          showPasswordModal.value = false;
          
          // Forzar una actualización del caché de mantenimiento
          await maintenanceService.resetMaintenanceCache(true);
          
          // Redirigir a la página principal
          router.push({ 
            name: 'Home',
            query: { t: Date.now() } // Añadir timestamp para evitar caché de navegación
          });
        } else {
          error.value = result.message || 'Contraseña incorrecta';
        }
      } catch (err) {
        error.value = 'Error al validar la contraseña. Intente nuevamente.';
        console.error('Error al validar contraseña:', err);
      } finally {
        validating.value = false;
      }
    };
    
    onMounted(() => {
      checkMaintenanceStatus();
    });
    
    return {
      message,
      defaultMessage,
      loadingPage,
      showPasswordModal,
      hasBypassPassword,
      password,
      error,
      validating,
      checkMaintenanceStatus,
      validatePassword,
      goToAdminLogin
    };
  }
};
</script>

<style scoped>
.maintenance-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 2rem;
  background-color: var(--black-color-2, #141414);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.maintenance-card {
  max-width: 600px;
  padding: 2.5rem;
  border-radius: 12px;
  background-color: var(--black-color-3, #1c1c1c);
  border: 1px solid rgba(255, 255, 255, 0.08);
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}

.maintenance-logo {
  max-width: 280px;
  margin-bottom: 2rem;
}

.maintenance-icon {
  font-size: 4rem;
  margin-bottom: 1.5rem;
  color: var(--primary-color);
}

h1 {
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: var(--white-color, #ffffff);
}

p {
  font-size: 1.1rem;
  margin-bottom: 1rem;
  color: var(--white-color-2, #bdbdbd);
}

.maintenance-message {
  margin-top: 1.5rem;
  font-weight: 500;
}

.maintenance-action {
  margin-top: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
}

.btn {
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 280px;
}

.btn-primary {
  background-color: var(--primary-color);
  border: 1px solid var(--primary-color);
  color: var(--white-color, #ffffff);
  margin-bottom: 0.5rem;
}

.btn-primary:hover {
  background-color: #6a0093;
  border-color: #6a0093;
}

.btn-outline-primary {
  background-color: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
}

.btn-outline-primary:hover {
  background-color: var(--primary-color);
  color: var(--white-color, #ffffff);
}

.btn-outline-secondary {
  background-color: transparent;
  border: 1px solid var(--white-color-2, #bdbdbd);
  color: var(--white-color-2, #bdbdbd);
}

.btn-outline-secondary:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.loader-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loader {
  width: 50px;
  height: 50px;
  border: 3px solid var(--primary-color);
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: var(--black-color-3, #131313);
  border-radius: 8px;
  padding: 2rem;
  width: 90%;
  max-width: 400px;
}

.modal-content h3 {
  color: var(--white-color, #ffffff);
  margin-bottom: 1rem;
}

.modal-content p {
  color: var(--white-color-2, #bdbdbd);
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  background-color: var(--black-color-2, #2b2b2b);
  border: 1px solid var(--grey-color, #d2d2d2bb);
  border-radius: 4px;
  color: var(--white-color, #ffffff);
  font-size: 1rem;
}

.form-control:focus {
  border-color: var(--primary-color);
  outline: none;
}

.error-message {
  color: #ff4d4f;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 576px) {
  .maintenance-card {
    padding: 1.5rem;
  }

  h1 {
    font-size: 1.6rem;
  }

  p {
    font-size: 1rem;
  }
}
</style>
